const QR_API_BASE = 'https://api.qrserver.com/v1/create-qr-code/';
const QR_API_MAX_SIZE = 1000;
const SCAN_TEXT = 'For Nursing Service Scan Here.';
const ROOM_NO_PREFIX = 'Room No:';

/**
 * Layout matched to client visual ("first image" style) on A4 595×842.
 * Border 540.72×650; logo ~97×103; QR ~300 (not edge-to-edge 388 — that looked too big).
 * Keep in sync with RN_Live_Agent RoomQrCard.tsx.
 */
const A4_PT = {
    pageWidth: 595,
    pageHeight: 842,
    borderX: 28,
    borderY: 96,
    borderW: 540.72,
    borderH: 650,
    borderStroke: 1.94,
    // Slightly larger than 96.97×103.29 for clearer logo presence
    logoW: 112,
    logoH: 119.3,
    logoTop: 118,
    // First-image style: QR ~55% of frame width (was 387.88 and looked oversized)
    qr: 298.3,
    qrTop: 281.5,
    textW: 416.6,
    textH: 27.05,
    // Both lines use the same size (Room No no longer looks larger via bold-only)
    fontSize: 23.27,
    scanTop: 647,
    roomTop: 693.4,
};

const PT_TO_PX = 2480 / 595;
const pt = (value) => value * PT_TO_PX;
const CM_TO_PT = 72 / 2.54;
const ptToCm = (value) => (value * 2.54) / 72;

const A4_WIDTH = Math.round(pt(A4_PT.pageWidth)); // 2480
const A4_HEIGHT = Math.round(pt(A4_PT.pageHeight)); // 3508

/** Small card: 12 cm × 12 cm square (print size). */
const SMALL_SIDE_CM = 12;
const SMALL_SIDE_PT = SMALL_SIDE_CM * CM_TO_PT; // ~340.16 pt
/** Raster for 12 cm at 300 DPI */
const SMALL_SIDE_PX = Math.round((SMALL_SIDE_CM / 2.54) * 300); // ~1417

const A4_PDF_LAYOUT = {
    pageWidth: A4_WIDTH,
    pageHeight: A4_HEIGHT,
    borderInsetX: pt(A4_PT.borderX),
    borderTop: pt(A4_PT.borderY),
    borderBottom: pt(A4_PT.borderY + A4_PT.borderH),
    borderWidth: pt(A4_PT.borderStroke),
    logoWidth: pt(A4_PT.logoW),
    logoHeight: pt(A4_PT.logoH),
    logoTop: pt(A4_PT.logoTop),
    qrSize: pt(A4_PT.qr),
    qrTop: pt(A4_PT.qrTop),
    textWidth: pt(A4_PT.textW),
    textHeight: pt(A4_PT.textH),
    scanFontSize: pt(A4_PT.fontSize),
    scanBaseline: pt(A4_PT.scanTop + A4_PT.fontSize),
    roomFontSize: pt(A4_PT.fontSize),
    roomBaseline: pt(A4_PT.roomTop + A4_PT.fontSize),
};

/** @deprecated kept for preview scaling helpers */
const SMALL_EXPORT_WIDTH = SMALL_SIDE_PX;

const loadImage = (src) => new Promise((resolve, reject) => {
    const image = new Image();
    image.crossOrigin = 'anonymous';
    image.onload = () => resolve(image);
    image.onerror = reject;
    image.src = src;
});

export const buildInPatientRoomUrl = (room, meetURL) => {
    const encryptRoomId = room && room.encryptRoomId;
    const meetBase = meetURL.replace('custom/', '');
    // customer/srm_ip_room/list expects Users.encrypt_id — never put numeric vendor.id
    // (srm_ip_room.vendor_id) in :vendor_id or the patient page gets HTTP 403.
    const vendorEncryptId = room && (
        room.vendorEncryptId
        || room.vendor_encrypt_id
        || room.encryptVendorId
        || room.encrypt_vendor_id
    );
    // Bedside QR should open the room service page directly.
    if (encryptRoomId) {
        if (vendorEncryptId) {
            return `${meetBase}SRM_IP/patient/${vendorEncryptId}/${encryptRoomId}`;
        }
        return `${meetBase}SRM_IP/custom/${encryptRoomId}`;
    }
    if (vendorEncryptId) {
        return `${meetBase}SRM_IP/patient/${vendorEncryptId}`;
    }
    return `${meetBase}SRM_IP/custom/`;
};

export const buildRoomQrLabel = (room) => {
    const roomName = room && room.patientRoomName ? String(room.patientRoomName).trim() : '';
    if (roomName) {
        return roomName;
    }
    if (room && room.patientRoomId != null) {
        return String(room.patientRoomId);
    }
    return '-';
};

export const buildQrCodeImageUrl = (patientUrl, size = 250) => {
    const safeSize = Math.max(80, Math.min(QR_API_MAX_SIZE, Math.round(size)));
    // Quiet zone so modules don't look oversized inside the QR box (matches first-image style).
    return `${QR_API_BASE}?data=${encodeURIComponent(patientUrl)}&size=${safeSize}x${safeSize}&margin=2&ecc=M`;
};

export const scaleA4PdfLayout = (targetWidth) => {
    const scale = targetWidth / A4_PDF_LAYOUT.pageWidth;
    const scaled = {};
    Object.keys(A4_PDF_LAYOUT).forEach((key) => {
        scaled[key] = A4_PDF_LAYOUT[key] * scale;
    });
    scaled.pageWidth = targetWidth;
    scaled.pageHeight = A4_PDF_LAYOUT.pageHeight * scale;
    return scaled;
};

const drawA4Page = (ctx, { roomLabel, logoImage, qrImage, layout }) => {
    const {
        pageWidth,
        pageHeight,
        borderInsetX,
        borderTop,
        borderBottom,
        borderWidth,
        logoWidth,
        logoHeight,
        logoTop,
        qrSize,
        qrTop,
        scanFontSize,
        scanBaseline,
        roomFontSize,
        roomBaseline,
    } = layout;

    const centerX = pageWidth / 2;

    ctx.fillStyle = '#FFFFFF';
    ctx.fillRect(0, 0, pageWidth, pageHeight);

    ctx.strokeStyle = '#E1E1E1';
    ctx.lineWidth = Math.max(1, borderWidth);
    ctx.strokeRect(
        borderInsetX,
        borderTop,
        pageWidth - borderInsetX * 2,
        borderBottom - borderTop
    );

    ctx.drawImage(
        logoImage,
        centerX - logoWidth / 2,
        logoTop,
        logoWidth,
        logoHeight
    );

    ctx.drawImage(
        qrImage,
        centerX - qrSize / 2,
        qrTop,
        qrSize,
        qrSize
    );

    ctx.textAlign = 'center';
    ctx.textBaseline = 'alphabetic';
    // Same size for both lines
    ctx.fillStyle = '#000000';
    ctx.font = `700 ${Math.round(scanFontSize)}px Arial, Helvetica, sans-serif`;
    ctx.fillText(SCAN_TEXT, centerX, scanBaseline);

    ctx.font = `700 ${Math.round(roomFontSize)}px Arial, Helvetica, sans-serif`;
    ctx.fillText(`${ROOM_NO_PREFIX} ${roomLabel}`, centerX, roomBaseline);

    return pageHeight;
};

export const generateRoomQrCardCanvas = async ({
    patientUrl,
    roomLabel,
    variant = 'small',
    logoSrc = `${process.env.PUBLIC_URL || ''}/srm_qr_logo.png`,
}) => {
    const layout = A4_PDF_LAYOUT;
    const qrFetchSize = Math.min(QR_API_MAX_SIZE, Math.round(layout.qrSize));
    const qrImage = await loadImage(buildQrCodeImageUrl(patientUrl, qrFetchSize));
    const logoImage = await loadImage(logoSrc);

    const a4Canvas = document.createElement('canvas');
    const a4Ctx = a4Canvas.getContext('2d');
    a4Ctx.imageSmoothingEnabled = true;
    a4Ctx.imageSmoothingQuality = 'high';
    a4Canvas.width = Math.round(layout.pageWidth);
    a4Canvas.height = Math.round(layout.pageHeight);
    drawA4Page(a4Ctx, {
        roomLabel,
        logoImage,
        qrImage,
        layout,
    });

    if (variant === 'a4') {
        return a4Canvas;
    }

    // Small = 12 cm × 12 cm square; fit A4 design centered
    const side = SMALL_SIDE_PX;
    const canvas = document.createElement('canvas');
    const ctx = canvas.getContext('2d');
    canvas.width = side;
    canvas.height = side;
    ctx.fillStyle = '#FFFFFF';
    ctx.fillRect(0, 0, side, side);
    ctx.imageSmoothingEnabled = true;
    ctx.imageSmoothingQuality = 'high';
    const fit = side / layout.pageHeight;
    const drawW = layout.pageWidth * fit;
    const drawX = (side - drawW) / 2;
    ctx.drawImage(a4Canvas, drawX, 0, drawW, side);
    return canvas;
};

const canvasToBlob = (canvas, type = 'image/png', quality) => new Promise((resolve, reject) => {
    canvas.toBlob((blob) => {
        if (blob) {
            resolve(blob);
            return;
        }
        reject(new Error('Unable to create QR card image'));
    }, type, quality);
});

/** Single-page PDF with embedded JPEG (no extra library). */
export const buildPdfFromJpegBytes = (jpegBytes, imgWidthPx, imgHeightPx, pageWidthPt, pageHeightPt) => {
    const encoder = new TextEncoder();
    const jpeg = jpegBytes instanceof Uint8Array ? jpegBytes : new Uint8Array(jpegBytes);

    const objects = [];
    const addObject = (content) => {
        objects.push(content);
        return objects.length;
    };

    addObject('<< /Type /Catalog /Pages 2 0 R >>');
    addObject('<< /Type /Pages /Kids [3 0 R] /Count 1 >>');
    addObject(
        `<< /Type /Page /Parent 2 0 R /MediaBox [0 0 ${pageWidthPt} ${pageHeightPt}] `
        + `/Resources << /XObject << /Im0 5 0 R >> >> /Contents 4 0 R >>`
    );
    const contentStream = `q\n${pageWidthPt} 0 0 ${pageHeightPt} 0 0 cm\n/Im0 Do\nQ\n`;
    addObject(
        `<< /Length ${contentStream.length} >>\nstream\n${contentStream}endstream`
    );
    const imageDict =
        `<< /Type /XObject /Subtype /Image /Width ${imgWidthPx} /Height ${imgHeightPx} `
        + `/ColorSpace /DeviceRGB /BitsPerComponent 8 /Filter /DCTDecode /Length ${jpeg.length} >>\n`
        + `stream\n`;
    addObject({ dict: imageDict, binary: jpeg, end: '\nendstream' });

    const parts = [];
    const pushStr = (str) => {
        parts.push(encoder.encode(str));
    };

    pushStr('%PDF-1.4\n');
    const offsets = [0];

    for (let i = 0; i < objects.length; i += 1) {
        offsets.push(parts.reduce((sum, p) => sum + p.length, 0));
        const obj = objects[i];
        pushStr(`${i + 1} 0 obj\n`);
        if (obj && obj.binary) {
            pushStr(obj.dict);
            parts.push(obj.binary);
            pushStr(obj.end);
            pushStr('\nendobj\n');
        } else {
            pushStr(`${obj}\nendobj\n`);
        }
    }

    const xrefOffset = parts.reduce((sum, p) => sum + p.length, 0);
    pushStr(`xref\n0 ${objects.length + 1}\n`);
    pushStr('0000000000 65535 f \n');
    for (let i = 1; i <= objects.length; i += 1) {
        pushStr(`${String(offsets[i]).padStart(10, '0')} 00000 n \n`);
    }
    pushStr(`trailer\n<< /Size ${objects.length + 1} /Root 1 0 R >>\n`);
    pushStr(`startxref\n${xrefOffset}\n%%EOF\n`);

    const total = parts.reduce((sum, p) => sum + p.length, 0);
    const out = new Uint8Array(total);
    let offset = 0;
    parts.forEach((part) => {
        out.set(part, offset);
        offset += part.length;
    });
    return out;
};

const imageToJpegBytes = async (image, width, height, quality = 0.98) => {
    const canvas = document.createElement('canvas');
    canvas.width = Math.max(1, Math.round(width));
    canvas.height = Math.max(1, Math.round(height));
    const ctx = canvas.getContext('2d');
    ctx.fillStyle = '#FFFFFF';
    ctx.fillRect(0, 0, canvas.width, canvas.height);
    ctx.imageSmoothingEnabled = true;
    ctx.imageSmoothingQuality = 'high';
    ctx.drawImage(image, 0, 0, canvas.width, canvas.height);
    const blob = await canvasToBlob(canvas, 'image/jpeg', quality);
    return {
        bytes: new Uint8Array(await blob.arrayBuffer()),
        width: canvas.width,
        height: canvas.height,
    };
};

const escapePdfText = (value) => String(value)
    .replace(/\\/g, '\\\\')
    .replace(/\(/g, '\\(')
    .replace(/\)/g, '\\)');

const measurePdfTextWidth = (text, fontSize, fontWeight = '400') => {
    const canvas = document.createElement('canvas');
    const ctx = canvas.getContext('2d');
    ctx.font = `${fontWeight} ${fontSize}px Helvetica, Arial, sans-serif`;
    return ctx.measureText(text).width;
};

/**
 * Build QR PDF like client reference: vector border + text, separate logo/QR images.
 * @param {object} options
 * @param {'a4'|'small'} [options.variant='a4'] - a4 = 21×29.7 cm, small = 12×12 cm
 */
export const buildStructuredRoomQrPdf = async ({
    patientUrl,
    roomLabel,
    logoSrc = `${process.env.PUBLIC_URL || ''}/srm_qr_logo.png`,
    variant = 'a4',
    scale, // legacy: 1=a4, 0.5 treated as small
}) => {
    const isSmall = variant === 'small' || Number(scale) === 0.5;
    // Draw in full A4 point coords, then scale into target page.
    const designW = A4_PT.pageWidth;
    const designH = A4_PT.pageHeight;
    const pageW = isSmall ? SMALL_SIDE_PT : designW;
    const pageH = isSmall ? SMALL_SIDE_PT : designH;
    // Fit design into page (small = square 12 cm; scale by height so full card fits)
    const fit = Math.min(pageW / designW, pageH / designH);
    const contentW = designW * fit;
    const contentH = designH * fit;
    const ox = (pageW - contentW) / 2;
    const oy = (pageH - contentH) / 2;

    const scanText = SCAN_TEXT;
    const roomText = `${ROOM_NO_PREFIX} ${roomLabel}`;
    const logoX = (designW - A4_PT.logoW) / 2;
    const qrX = (designW - A4_PT.qr) / 2;

    const logoImage = await loadImage(logoSrc);
    const qrImage = await loadImage(buildQrCodeImageUrl(patientUrl, QR_API_MAX_SIZE));
    const logoJpeg = await imageToJpegBytes(logoImage, A4_PT.logoW * 4, A4_PT.logoH * 4, 0.98);
    const qrJpeg = await imageToJpegBytes(qrImage, QR_API_MAX_SIZE, QR_API_MAX_SIZE, 0.98);

    // Design-space PDF y (bottom-up on A4 design canvas)
    const pdfY = (top, height = 0) => designH - top - height;
    const outerX = A4_PT.borderX - A4_PT.borderStroke;
    const outerY = A4_PT.borderY - A4_PT.borderStroke;
    const outerW = A4_PT.borderW + A4_PT.borderStroke * 2;
    const outerH = A4_PT.borderH + A4_PT.borderStroke * 2;

    const scanFont = A4_PT.fontSize;
    const roomFont = A4_PT.fontSize;
    const scanWidth = measurePdfTextWidth(scanText, scanFont, '700');
    const roomWidth = measurePdfTextWidth(roomText, roomFont, '700');
    const scanPdfX = (designW - scanWidth) / 2;
    const roomPdfX = (designW - roomWidth) / 2;
    const scanPdfY = pdfY(A4_PT.scanTop + A4_PT.textH - 4);
    const roomPdfY = pdfY(A4_PT.roomTop + A4_PT.textH - 4);

    const designOps = [
        '1 1 1 rg',
        `0 0 ${designW} ${designH} re f`,
        '0.883 0.883 0.883 rg',
        `${outerX.toFixed(2)} ${pdfY(outerY, outerH).toFixed(2)} ${outerW.toFixed(2)} ${outerH.toFixed(2)} re f`,
        '1 1 1 rg',
        `${A4_PT.borderX.toFixed(2)} ${pdfY(A4_PT.borderY, A4_PT.borderH).toFixed(2)} ${A4_PT.borderW.toFixed(2)} ${A4_PT.borderH.toFixed(2)} re f`,
        'q',
        `${A4_PT.logoW.toFixed(2)} 0 0 ${A4_PT.logoH.toFixed(2)} ${logoX.toFixed(2)} ${pdfY(A4_PT.logoTop, A4_PT.logoH).toFixed(2)} cm`,
        '/ImLogo Do',
        'Q',
        'q',
        `${A4_PT.qr.toFixed(2)} 0 0 ${A4_PT.qr.toFixed(2)} ${qrX.toFixed(2)} ${pdfY(A4_PT.qrTop, A4_PT.qr).toFixed(2)} cm`,
        '/ImQr Do',
        'Q',
        'BT',
        `/F1 ${scanFont.toFixed(2)} Tf`,
        '0 0 0 rg',
        `1 0 0 1 ${scanPdfX.toFixed(2)} ${scanPdfY.toFixed(2)} Tm`,
        `(${escapePdfText(scanText)}) Tj`,
        'ET',
        'BT',
        `/F1 ${roomFont.toFixed(2)} Tf`,
        '0 0 0 rg',
        `1 0 0 1 ${roomPdfX.toFixed(2)} ${roomPdfY.toFixed(2)} Tm`,
        `(${escapePdfText(roomText)}) Tj`,
        'ET',
    ];

    const contentLines = [
        'q',
        // White page background
        '1 1 1 rg',
        `0 0 ${pageW.toFixed(2)} ${pageH.toFixed(2)} re f`,
        // Scale + center A4 design into target page (12×12 for small)
        `${fit.toFixed(6)} 0 0 ${fit.toFixed(6)} ${ox.toFixed(2)} ${oy.toFixed(2)} cm`,
        ...designOps,
        'Q',
    ];
    const contentStream = `${contentLines.join('\n')}\n`;

    const encoder = new TextEncoder();
    const objects = [];
    const addObject = (content) => {
        objects.push(content);
        return objects.length;
    };

    // 1 cat, 2 pages, 3 page, 4 content, 5 F1, 6 logo, 7 qr
    addObject('<< /Type /Catalog /Pages 2 0 R >>');
    addObject('<< /Type /Pages /Kids [3 0 R] /Count 1 >>');
    addObject(
        '<< /Type /Page /Parent 2 0 R '
        + `/MediaBox [0 0 ${pageW} ${pageH}] `
        + '/Resources << '
        + '/Font << /F1 5 0 R >> '
        + '/XObject << /ImLogo 6 0 R /ImQr 7 0 R >> '
        + '>> /Contents 4 0 R >>'
    );
    addObject(`<< /Length ${contentStream.length} >>\nstream\n${contentStream}endstream`);
    addObject('<< /Type /Font /Subtype /Type1 /BaseFont /Helvetica-Bold >>');
    addObject({
        dict:
            `<< /Type /XObject /Subtype /Image /Width ${logoJpeg.width} /Height ${logoJpeg.height} `
            + `/ColorSpace /DeviceRGB /BitsPerComponent 8 /Filter /DCTDecode /Length ${logoJpeg.bytes.length} >>\n`
            + 'stream\n',
        binary: logoJpeg.bytes,
        end: '\nendstream',
    });
    addObject({
        dict:
            `<< /Type /XObject /Subtype /Image /Width ${qrJpeg.width} /Height ${qrJpeg.height} `
            + `/ColorSpace /DeviceRGB /BitsPerComponent 8 /Filter /DCTDecode /Length ${qrJpeg.bytes.length} >>\n`
            + 'stream\n',
        binary: qrJpeg.bytes,
        end: '\nendstream',
    });

    const parts = [];
    const pushStr = (str) => {
        parts.push(encoder.encode(str));
    };
    pushStr('%PDF-1.4\n');
    const offsets = [0];
    for (let i = 0; i < objects.length; i += 1) {
        offsets.push(parts.reduce((sum, p) => sum + p.length, 0));
        const obj = objects[i];
        pushStr(`${i + 1} 0 obj\n`);
        if (obj && obj.binary) {
            pushStr(obj.dict);
            parts.push(obj.binary);
            pushStr(obj.end);
            pushStr('\nendobj\n');
        } else {
            pushStr(`${obj}\nendobj\n`);
        }
    }
    const xrefOffset = parts.reduce((sum, p) => sum + p.length, 0);
    pushStr(`xref\n0 ${objects.length + 1}\n`);
    pushStr('0000000000 65535 f \n');
    for (let i = 1; i <= objects.length; i += 1) {
        pushStr(`${String(offsets[i]).padStart(10, '0')} 00000 n \n`);
    }
    pushStr(`trailer\n<< /Size ${objects.length + 1} /Root 1 0 R >>\n`);
    pushStr(`startxref\n${xrefOffset}\n%%EOF\n`);

    const total = parts.reduce((sum, p) => sum + p.length, 0);
    const out = new Uint8Array(total);
    let offset = 0;
    parts.forEach((part) => {
        out.set(part, offset);
        offset += part.length;
    });
    return out;
};

const triggerBlobDownload = (blob, fileName) => {
    const blobUrl = URL.createObjectURL(blob);
    const link = document.createElement('a');
    link.href = blobUrl;
    link.download = fileName;
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
    URL.revokeObjectURL(blobUrl);
};

export const generateRoomQrCardBlob = async (options) => {
    const canvas = await generateRoomQrCardCanvas(options);
    return canvasToBlob(canvas, 'image/png');
};

export const downloadRoomQrCard = async ({
    patientUrl,
    roomLabel,
    downloadName,
    variant = 'small',
    format = 'png',
}) => {
    const safeName = String(downloadName || 'room').replace(/[^\w.-]+/g, '_');
    const suffix = variant === 'a4' ? 'A4' : 'Small';
    const fileFormat = String(format || 'png').toLowerCase() === 'pdf' ? 'pdf' : 'png';
    const fileName = `${safeName}_QRCode_${suffix}.${fileFormat}`;

    // PDF: A4 = 21×29.7 cm; Small = 12×12 cm square
    if (fileFormat === 'pdf') {
        const pdfBytes = await buildStructuredRoomQrPdf({
            patientUrl,
            roomLabel,
            variant: variant === 'a4' ? 'a4' : 'small',
        });
        triggerBlobDownload(new Blob([pdfBytes], { type: 'application/pdf' }), fileName);
        return fileName;
    }

    const canvas = await generateRoomQrCardCanvas({ patientUrl, roomLabel, variant });
    const blob = await canvasToBlob(canvas, 'image/png');
    triggerBlobDownload(blob, fileName);
    return fileName;
};

export const getRoomQrCardStyles = (variant = 'small') => {
    const layout = variant === 'a4'
        ? scaleA4PdfLayout(794)
        : scaleA4PdfLayout(Math.round(A4_WIDTH * (SMALL_SIDE_PT / A4_PT.pageHeight)));
    const borderWidth = Math.max(1, layout.borderWidth);
    return {
        page: {
            position: 'relative',
            width: `${layout.pageWidth}px`,
            height: `${layout.pageHeight}px`,
            backgroundColor: '#FFFFFF',
            boxSizing: 'border-box',
        },
        frame: {
            position: 'absolute',
            left: `${layout.borderInsetX}px`,
            top: `${layout.borderTop}px`,
            width: `${layout.pageWidth - layout.borderInsetX * 2}px`,
            height: `${layout.borderBottom - layout.borderTop}px`,
            borderWidth: `${borderWidth}px`,
            borderColor: '#E1E1E1',
            borderStyle: 'solid',
            boxSizing: 'border-box',
            pointerEvents: 'none',
        },
        logo: {
            position: 'absolute',
            left: '50%',
            top: `${layout.logoTop}px`,
            width: `${layout.logoWidth}px`,
            height: `${layout.logoHeight}px`,
            transform: 'translateX(-50%)',
        },
        qr: {
            position: 'absolute',
            left: '50%',
            top: `${layout.qrTop}px`,
            width: `${layout.qrSize}px`,
            height: `${layout.qrSize}px`,
            transform: 'translateX(-50%)',
        },
        scanText: {
            position: 'absolute',
            left: '50%',
            top: `${layout.scanBaseline - layout.scanFontSize}px`,
            width: `${layout.textWidth}px`,
            height: `${layout.textHeight}px`,
            transform: 'translateX(-50%)',
            fontSize: `${layout.scanFontSize}px`,
            lineHeight: `${layout.textHeight}px`,
            color: '#000000',
            textAlign: 'center',
            fontFamily: 'Arial, Helvetica, sans-serif',
            fontWeight: '700',
            margin: 0,
        },
        roomText: {
            position: 'absolute',
            left: '50%',
            top: `${layout.roomBaseline - layout.roomFontSize}px`,
            width: `${layout.textWidth}px`,
            height: `${layout.textHeight}px`,
            transform: 'translateX(-50%)',
            fontSize: `${layout.roomFontSize}px`,
            lineHeight: `${layout.textHeight}px`,
            fontWeight: '700',
            color: '#000000',
            textAlign: 'center',
            fontFamily: 'Arial, Helvetica, sans-serif',
            margin: 0,
        },
    };
};

export const A4_EXPORT_WIDTH = A4_WIDTH;
export const A4_EXPORT_HEIGHT = A4_HEIGHT;
export const SMALL_EXPORT_WIDTH_PX = SMALL_SIDE_PX;
export const SMALL_SIDE_CM_SIZE = SMALL_SIDE_CM;
export const getQrCardSizeCm = (variant = 'a4') => {
    if (variant === 'small') {
        return { widthCm: SMALL_SIDE_CM, heightCm: SMALL_SIDE_CM };
    }
    return {
        widthCm: Math.round(ptToCm(A4_PT.pageWidth) * 10) / 10,
        heightCm: Math.round(ptToCm(A4_PT.pageHeight) * 10) / 10,
    };
};
export { A4_PDF_LAYOUT };
